/**
 * Valid layout types for section elements
 */
type SectionElementLayoutTypes = 'overview' | 'one-column' | 'two-rows' | 'two-columns';

/**
 * Base type for all parsed elements
 * Provides the fundamental structure shared across all element types
 */
export interface BaseElementType {
	/** Optional attributes map for element configuration */
	attributes?: Record<string, string | undefined>;
	/** Child elements or text content */
	children?: Array<ParsedElementType> | string;
	/** Element tag identifier */
	tag: string;
}

// ============================================================================
// Sub-agent Data Types
// ============================================================================

/**
 * Union of all supported sub-agent names
 * Add new sub-agents here to ensure type safety across the system
 */
export type SUB_AGENTS = 'ENTITY_RELATED_PEOPLE' | 'PROJECT_HIGHLIGHTS';

/**
 * Raw sub-agent response from the backend
 * Contains unparsed JSON data that needs to be processed
 */
export interface LuminaSubAgentResponseType {
	/** JSON string containing the sub-agent's response data */
	data: string;
	/** Identifier for which sub-agent produced this response */
	sub_agent_name: SUB_AGENTS;
}

/**
 * Individual project highlight item
 */
export interface ProjectHighlight {
	/** Detailed description of the highlight */
	content: string;
	/** Title of the highlight */
	title: string;
}

/**
 * Parsed data from PROJECT_HIGHLIGHTS sub-agent
 * Contains structured information about project milestones and achievements
 */
export interface ProjectHighlightsData {
	/** Array of highlight items */
	highlights: ProjectHighlight[];
	/** Project name */
	name: string;
	/** High-level project summary */
	summary: string;
}

/**
 * Individual person data from the people directory
 * Contains comprehensive employee/user information
 */
export type PersonData = {
	/** Atlassian account identifier */
	accountId: string;
	/** Account status (active, inactive, etc.) */
	accountStatus: string;
	/** Atlassian Resource Identifier */
	ari: string;
	/** URL to the person's avatar image */
	avatarUrl: string;
	/** Department or organizational unit */
	department: string;
	/** Email address */
	email: string;
	/** Unique identifier */
	id: string;
	/** Job title or role */
	jobTitle: string;
	/** Physical or remote location */
	location: string;
	/** Full name */
	name: string;
	/** Organization name */
	organization: string;
	/** Optional Slack direct message link */
	slackMessage: string;
	/** Display title */
	title: string;
	/** URL to the person's profile */
	url: string;
};

/**
 * Parsed data from ENTITY_RELATED_PEOPLE sub-agent
 * Contains a list of people relevant to the current context
 */
export type RelatedPeopleData = {
	/** Array of related people */
	people: PersonData[];
};

/**
 * Sub-agent data map for passing parsed sub-agent responses as props
 *
 * This map is created once in LuminaLayout via useMemo and passed down through the
 * component tree, eliminating the need for React Context. Each sub-agent's parsed
 * data is stored under its corresponding key.
 *
 * @example
 * ```tsx
 * // Parse once at the top level
 * const subAgentDataMap = useMemo((): ParsedSubAgentDataMap => {
 *   const map: ParsedSubAgentDataMap = {};
 *   luminaSubAgentResponse.forEach((response) => {
 *     if (response.sub_agent_name === 'PROJECT_HIGHLIGHTS') {
 *       map.projectHighlights = JSON.parse(response.data);
 *     }
 *   });
 *   return map;
 * }, [luminaSubAgentResponse]);
 *
 * // Pass down through props (no Context needed)
 * <LuminaSection subAgentDataMap={subAgentDataMap} />
 * ```
 */
export interface ParsedSubAgentDataMap {
	/** Parsed project highlights data, if available */
	projectHighlights?: ProjectHighlightsData;
	/** Parsed related people data, if available */
	relatedPeople?: RelatedPeopleData;
}

// ============================================================================
// Element Types
// ============================================================================

/**
 * Section element that contains other elements and defines layout structure
 * Sections are the top-level containers that determine how child elements are arranged
 */
export interface SectionElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Indicates if section should be collapsible ('1' = foldable) */
		folded?: '1' | undefined;
		/** Layout strategy for arranging child elements */
		layout: SectionElementLayoutTypes;
		/** Unique section identifier */
		n: string;
	};
	tag: 'section' | string;
}

/**
 * Markdown content element for rich text rendering
 * Supports standard markdown syntax for formatted text
 */
export interface MarkdownElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'md';
}

/**
 * Header element for section titles and headings
 * Supports h1-h6 semantic heading levels
 */
export interface HeaderElementType extends BaseElementType {
	attributes?: {
		[key: string]: string;
		/** Header text content */
		header: string;
		/** Heading level (1-6 for h1-h6) */
		level: '1' | '2' | '3' | '4' | '5' | '6' | string;
	};
	tag: 'h';
}

/**
 * Type guard to check if an element is a header
 * @param element - Element to check
 * @returns True if element is a HeaderElementType
 */
export function isHeaderElement(element: ParsedElementType): element is HeaderElementType {
	return typeof element !== 'string' && element.tag === 'h';
}

/**
 * Carousel element for displaying multiple items in a scrollable container
 * Used for collections like smart links, people cards, or project highlights
 */
export interface CarouselElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Display mode for the carousel layout (e.g., 'two-column' for wider cards) */
		display?: 'two-column';
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		/** Carousel title/heading */
		title: string;
	};
	tag: 'carousel';
}

// Smart link display types for ADF conversion
export type SmartLinkDisplayType = 'inline' | 'block' | 'embed';

/**
 * Smart link element for rich link previews
 * Displays contextual information about URLs (Confluence pages, Jira issues, etc.)
 * The `height` and `width` attributes are embed-specific for controlling embedded content dimensions.
 */
export interface SmartLinkElementType extends BaseElementType {
	attributes?: {
		/** Display type for ADF conversion */
		display?: SmartLinkDisplayType;
		/** Height for embedded content */
		height?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		/** Link title or display text */
		title?: string;
		/** Link type classification */
		type?: string;
		/** Target URL for the smart link */
		url?: string;
		width?: string;
	};
	tag: 'smart-link';
}

/**
 * Smart link list element
 * Contains a list of item elements, each with a URL, rendered as a vertical stack of smart link cards
 */
export interface SmartLinkListElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	children?: Array<ParsedElementType> | string;
	tag: 'smart-link-list';
}

/**
 * Single people entity — renders profile preview (Relay; see `people-entity-profile-relay`).
 * Attributes should include `account-id` or `accountId` (Atlassian account ID).
 */
export interface PeopleEntityElementType extends BaseElementType {
	tag: 'people-entity';
}

/**
 * List of people entities — renders inline previews per child (Relay; see `people-entity-inline-relay`).
 */
export interface PeopleEntityListElementType extends BaseElementType {
	children?: Array<ParsedElementType> | string;
	tag: 'people-entity-list';
}

/**
 * Action card element - a deprecated format that transforms to smart-link
 * @deprecated Transforms to SmartLinkElementType during rendering
 */
export interface ActionElementType extends BaseElementType {
	attributes?: {
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		/** Action button text */
		text?: string;
		/** Card title */
		title: string;
		/** Target URL (required for transformation to smart-link) */
		url?: string;
	};
	tag: 'action-card';
}

/**
 * Project highlight card element
 * Displays key achievements or milestones for a project
 */
export interface ProjectHighlightElementType extends BaseElementType {
	attributes?: {
		/** Detailed highlight content */
		content?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		/** Highlight title */
		title: string;
	};
	tag: 'project-highlight-card';
}

/**
 * Contact card element for displaying contact information
 * @deprecated Consider using PersonElementType instead
 */
export interface ContactElementType extends BaseElementType {
	attributes?: {
		/** URL to avatar/profile image */
		avatar_url?: string;
		/** Job title or role */
		job_title?: string;
		/** Contact message or description */
		message?: string;
		/** Contact name */
		name?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'contact-card';
}

/**
 * People card element for displaying person information
 * Used in related people carousels and team displays
 */
export interface PersonElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Atlassian account ID */
		account_id?: string;
		/** URL to avatar/profile image */
		avatar_url?: string;
		/** Email address */
		email?: string;
		/** Job title or role */
		job_title?: string;
		/** Physical or remote location */
		location?: string;
		/** Person's name */
		name?: string;
		/** URL to person's profile */
		url?: string;
	};
	tag: 'people-card';
}

/**
 * Reporting lines element for organizational hierarchy visualization
 * Displays manager/report relationships
 */
export interface ReportingLinesElementType extends BaseElementType {
	attributes?: {
		/** Entity/person identifier */
		id?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'report-lines';
}

/**
 * List element for ordered or unordered lists
 * Supports standard HTML list types (ul, ol)
 */
export interface ListElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		/** List type (ordered, unordered, etc.) */
		type?: string;
	};
	tag: 'ul' | 'ol' | 'list';
}

/**
 * List item element
 * Child element of ListElementType
 */
export interface ListItemElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
	};
	tag: 'li';
}

/**
 * Jira epic progress visualization element
 * Displays epic completion status and sub-issues
 */
export interface JiraEpicProgressElementType extends BaseElementType {
	attributes?: {
		/** Jira epic ID */
		id?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-epic-progress';
}

/**
 * Jira project burndown chart element
 * Visualizes sprint or project progress over time
 */
export interface JiraBurndownChartElementType extends BaseElementType {
	attributes?: {
		/** Jira project or sprint ID */
		id?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-burndown-chart';
}

/**
 * Jira work distribution element
 * Shows how work is distributed across team members or issue types
 */
export interface JiraWorkDistributionElementType extends BaseElementType {
	attributes?: {
		/** Jira project identifier */
		id?: string;
		/** Position hint for multi-column/row layouts */
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-work-distribution';
}

/**
 * Union type of all possible parsed element types
 * Includes string for text content and all structured element types
 */
export type ParsedElementType =
	| SectionElementType
	| ListElementType
	| MarkdownElementType
	| HeaderElementType
	| CarouselElementType
	| SmartLinkElementType
	| SmartLinkListElementType
	| PeopleEntityElementType
	| PeopleEntityListElementType
	| ActionElementType
	| PersonElementType
	| ProjectHighlightElementType
	| ContactElementType
	| ReportingLinesElementType
	| JiraEpicProgressElementType
	| JiraWorkDistributionElementType
	| JiraBurndownChartElementType
	| string;

/**
 * Parsed element types excluding string content
 * Useful for filtering and type narrowing
 */
export type ParsedStructuredElementType = Exclude<ParsedElementType, string>;

/**
 * Array of section elements representing a complete layout
 * This is the top-level structure for parsed Lumina content
 */
export type ParsedLayout = SectionElementType[];
